import { Icons } from '@agro-club/agroclub-shared'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { Deal } from 'modules/domain/deal/types'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton, ARouterLink } from 'views/components/Analytics'
import { SampleRequestType, SampleStatus, SampleWithRequests } from 'views/pages/Samples/types'
import { AddSampleModal } from '../AddSampleModal'
import { AddSampleRequestModal } from './AddSampleRequestModal'
import { History } from './History'
import { SampleForAccordion } from '../SampleForAccordion'
import { SampleRequest } from './SampleRequest'
import { RowGapWrapper } from '../styled'
import SamplesRoutes from 'views/pages/Samples/routes'
import { generatePath } from 'react-router'
import { Bid } from 'modules/domain/bid/types'
import { endpoints } from 'modules/endpoints'
import { CompleteSampleRequest } from 'views/pages/Bid/BidDetail/Bid/Samples/CompleteSampleRequest'

const ButtonsWrapper = styled.div`
  display: flex;
  column-gap: 12px;
  width: 100%;

  & > button {
    flex: 1;
  }
`

type Props = {
  deal?: Deal
  dealRefetch?: refetchFunc
  bid?: Bid
  bidId: number
  data?: SampleWithRequests[]
  currentSampleId?: number
  refetch: refetchFunc
}

export const SampleRequestBlock: FC<Props> = ({ deal, bid, bidId, data, refetch, currentSampleId, dealRefetch }) => {
  const { t } = useTranslation(['deal', 'common', 'samples'])

  const [addSampleRequest, setAddSampleRequest] = useState(false)
  const [addSample, setAddSample] = useState(false)
  const [editSample, setEditSample] = useState(false)
  const [readyToFetchBid, setReadyToFetchBid] = useState(false)
  // bidInternal probably can be deleted, we allow to create only on pages where we already had bid now
  const [progressBidInternal, bidInternal] = useSingleEntity<Bid>(readyToFetchBid ? endpoints.bid(bidId) : undefined)
  const [otherSamples, setOtherSamples] = useState<SampleWithRequests[]>()
  const [actualSample, setActualSample] = useState<SampleWithRequests>()

  const onAddSampleRequest = () => {
    if (!bidInternal && !readyToFetchBid) {
      setReadyToFetchBid(true)
    }
    setAddSampleRequest(true)
  }
  useEffect(() => {
    if (!data) return undefined
    if (currentSampleId) {
      const actual = data.filter((s) => s.id === currentSampleId)[0]
      setActualSample(actual)
      const filtered = data.filter((s) => s.id !== currentSampleId)
      setOtherSamples(filtered)
    } else {
      setActualSample(data?.[0])
      setOtherSamples(data.slice(1))
    }
  }, [data, currentSampleId])
  const sampleRequest = actualSample?.sample_requests[0]
  const hasHistory = !!sampleRequest?.updates?.length && sampleRequest.updates.length > 1
  const bidResult = bid || bidInternal
  const isActualSampleComplete =
    actualSample?.status === SampleStatus.published || actualSample?.status === SampleStatus.canceled

  const refetchSameplesAndDeal = async () => {
    await refetch()
    await dealRefetch?.()
  }
  return (
    <RowGapWrapper rowGap={12}>
      {!!otherSamples?.length && (
        <div>
          {t('samples:otherSamples')}
          {': '}
          {otherSamples
            .map<React.ReactNode>((sample) => {
              return (
                <ARouterLink
                  key={sample.id}
                  eventName="sample"
                  to={generatePath(SamplesRoutes.Details, {
                    id: sample.id,
                  })}
                >
                  {`${sample.title}`}
                </ARouterLink>
              )
            })
            .reduce((prev, curr) => [prev, '; ', curr])}
        </div>
      )}
      {!currentSampleId && (
        <ButtonsWrapper>
          <AButton
            id="addSampleRequest"
            size="big"
            color="approve"
            Icon={Icons.IconAdd}
            onClick={onAddSampleRequest}
            progress={progressBidInternal}
          >
            {t('accordion.sample.addText')}
          </AButton>
          <AButton
            id="addSample"
            size="big"
            color="secondary"
            background="none"
            border="solid"
            Icon={Icons.IconDescription}
            onClick={() => {
              if (actualSample && !isActualSampleComplete) {
                setEditSample(true)
              } else {
                setAddSample(true)
              }
            }}
          >
            {t('accordion.sample.addResults')}
          </AButton>
        </ButtonsWrapper>
      )}
      {actualSample && actualSample.id !== currentSampleId && actualSample.status === SampleStatus.published && (
        <SampleForAccordion actualSample={actualSample} sampleRefetch={refetch} />
      )}
      {hasHistory && <History sampleRequest={sampleRequest} sampleRequestRefetch={refetch} />}
      {sampleRequest && (
        <SampleRequest sampleRequest={sampleRequest as SampleRequestType} sampleRequestRefetch={refetch} />
      )}
      {addSampleRequest && bidResult && (
        <AddSampleRequestModal
          bid={bidResult}
          deal={deal}
          refetch={refetch}
          onClose={() => setAddSampleRequest(false)}
        />
      )}
      {addSample && (
        <AddSampleModal
          bidId={bidId.toString() ?? bid?.id}
          onClose={() => setAddSample(false)}
          refetch={refetchSameplesAndDeal}
        />
      )}
      {editSample && actualSample && (
        <CompleteSampleRequest
          sample={actualSample}
          refetch={refetchSameplesAndDeal}
          onClose={() => setEditSample(false)}
        />
      )}
    </RowGapWrapper>
  )
}
